import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import { ToastSample } from 'components/common/Toast/models/toast';
import ToastComponent from 'components/common/Toast/page';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils';
import { DELETE_ALL_ERROR, NO_SYMBOL, YES_SYMBOL } from '../constant';
import MenuServingTimeDetail from '../Detail';
import { MenuServingTimeItemList } from '../model/MenuServingTime';
import menuServingTimeService from '../services';
import './List.scss';

function MenuServingTime() {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // ref
  const btnAddRef = useRef<HTMLButtonElement>(null);
  const btnDetailRef = useRef<HTMLButtonElement>(null);
  const btnDeleteRef = useRef<HTMLButtonElement>(null);

  // state
  const [data, setData] = useState<MenuServingTimeItemList[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const [showMenuServingTimeDetail, setShowMenuServingTimeDetail] = useState(false);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);

  // api
  async function fecthMenuServingTimeList() {
    try {
      const response = await menuServingTimeService.getList();
      if (response.data) {
        setData(response.data);
      }
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  useEffect(() => {
    fecthMenuServingTimeList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function renderMenuServingTimeRow(item: MenuServingTimeItemList, index: number) {
    const rowStyle = {
      backgroundColor: item.delete_flg ? 'rgb(242, 242, 242)' : '#fff',
    };
    return (
      <tr key={item.id}>
        <td className="text-center" style={rowStyle}>
          {item.delete_flg && (
            <Button className="btn--orange" onClick={restoreDeletedItem.bind(null, item)}>
              {t('menuServingTime.button.restore')}
            </Button>
          )}
          {!item.delete_flg && (
            <Form.Check
              className="chk-menuservingtime-item custom-checkbox"
              data-type="menuservingtime"
              data-menuservingtime={item.name}
              value={item.id}
              checked={selectedItemIds.includes(item.id)}
              onChange={(e) => {
                const itemId = item.id;
                if (e.target.checked) {
                  setSelectedItemIds((prevIds) => [...prevIds, itemId]);
                } else {
                  setSelectedItemIds((prevIds) => prevIds.filter((id) => id !== itemId));
                }
              }}
            ></Form.Check>
          )}
        </td>
        <td className="text-center" style={rowStyle}>
          {item.name}
        </td>
        <td className="text-center" style={rowStyle}>
          {item.is_serving_time_first ? YES_SYMBOL : NO_SYMBOL}
        </td>
        <td className="text-center" style={rowStyle}>
          {item.is_serving_time_after_meal ? YES_SYMBOL : NO_SYMBOL}
        </td>
        <td className="text-center" style={rowStyle}>
          {item.is_serving_time_together ? YES_SYMBOL : NO_SYMBOL}
        </td>
        <td className="text-center" style={rowStyle}>
          {item.note}
        </td>
        <td className="text-center" style={rowStyle}>
          <Form.Check
            disabled={item.delete_flg}
            type="switch"
            style={{ color: '#38D145', fontSize: '20px' }}
            id={`status-switch-${index}`}
            className="custom-menu-serving-time-status-switch"
            checked={item.status}
            onChange={(e) => handleUpdateStatusButton(e, index)}
          />
        </td>
      </tr>
    );
  }

  async function restoreDeletedItem(item: MenuServingTimeItemList) {
    showGlobalModal(() => <AlertModal title={t('App.info')} message={t('menuServingTime.modal.restore')} />);
    const updatedData = data.map((dataItem) => {
      if (dataItem.id === item.id) {
        return { ...dataItem, delete_flg: false };
      }
      return dataItem;
    });
    try {
      const payload = updatedData;
      const response = await menuServingTimeService.updateList(payload);
      if (response) {
        setData(updatedData);
        setSelectedItemIds((prevIds) => prevIds.filter((id) => id !== item.id));
      }
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  async function processDelete(currentDefaultItem: MenuServingTimeItemList) {
    const selectedItemIdsSet = new Set(selectedItemIds);
    const activeItems = data.filter((item) => !item.delete_flg);
    const selectedActiveItemsToDelete = activeItems.filter((item) => selectedItemIdsSet.has(item.id));

    // prevent delete all items
    if (activeItems.length === selectedActiveItemsToDelete.length) {
      return DELETE_ALL_ERROR;
    }

    try {
      let updatedData = [...data];
      const isCurrentDefaultBeingDeleted = currentDefaultItem && selectedItemIdsSet.has(currentDefaultItem.id);

      // delete current default item
      if (isCurrentDefaultBeingDeleted) {
        // find items which are not deleted and not selected for deletion
        const remainingActiveItems = activeItems.filter((item) => !selectedItemIdsSet.has(item.id));
        // find new default item (ex : the lowest ID)
        const newDefaultItem = remainingActiveItems.find((item) => item.id !== currentDefaultItem?.id);

        if (newDefaultItem) {
          updatedData = updatedData.map((dataItem) => {
            if (dataItem.id === newDefaultItem.id) {
              return { ...dataItem, status: true };
            } else if (dataItem.id === currentDefaultItem!.id) {
              return { ...dataItem, delete_flg: true, status: false };
            } else if (selectedItemIdsSet.has(dataItem.id)) {
              return { ...dataItem, delete_flg: true };
            }
            return dataItem;
          });
        }
      }
      // delete other items
      else {
        updatedData = updatedData.map((dataItem) => {
          if (selectedItemIdsSet.has(dataItem.id)) {
            return { ...dataItem, delete_flg: true };
          }
          return dataItem;
        });
      }
      // prepare payloads for API call
      const payloads: Partial<MenuServingTimeItemList>[] = updatedData
        .filter((item) => selectedItemIdsSet.has(item.id) || item.delete_flg)
        .map((item) => ({
          id: item.id,
          delete_flg: item.delete_flg,
        }));
      const response = await menuServingTimeService.delete(payloads);
      if (response) {
        setData(updatedData);
        setSelectedItemIds([]);
        return true;
      }
    } catch (e) {
      return e;
    }
  }

  async function handleConfirmDelete(currentDefaultItem: MenuServingTimeItemList) {
    const result = await processDelete(currentDefaultItem);

    if (result === true) {
      showGlobalModal(() => <AlertModal title={t('App.info')} message={t('menuServingTime.modal.delete_success')} />);
    } else if (result === DELETE_ALL_ERROR) {
      showGlobalModal(() => <AlertModal title={t('App.error')} message={t('menuServingTime.modal.at_least_one')} />);
    } else if (result instanceof AxiosError) {
      if (!handleServiceError(showGlobalModal, result)) {
        showGlobalModal(() => <AlertModal title={t('App.error')} message={result.message} />);
      }
    } else {
    }
  }

  function handleCloseMenuServingTimeDetail() {
    setShowMenuServingTimeDetail(false);
    setEditingItemId(null);
    setSelectedItemIds([]);
  }
  function handleReflectUpdateNewItem(newItem: MenuServingTimeItemList) {
    setData((prevData) => {
      const existingIndex = prevData.findIndex((item) => item.id === newItem.id);
      if (existingIndex !== -1) {
        // update existing item
        const updatedData = [...prevData];
        updatedData[existingIndex] = newItem;
        return updatedData;
      } else {
        // add new item
        return [...prevData, newItem];
      }
    });
  }
  async function handleUpdateStatusButton(e: React.ChangeEvent<HTMLInputElement>, index: number) {
    const toggledItem = data[index];
    const newStatus = e.target.checked;
    // prevent user turn off all status toggle
    if (!newStatus) {
      const activeItems = data.filter((item) => item.status);
      if (activeItems.length === 1 && activeItems[0].id === toggledItem.id) {
        showGlobalModal(() => (
          <AlertModal title={t('App.error')} message={t('menuServingTime.modal.status_at_least_one')} />
        ));
        return;
      }
    }
    try {
      const payloads: { id: number; status: boolean }[] = [];
      let updatedLocalData = [...data];

      if (newStatus) {
        const currentlyActiveItem = data.find((item) => item.status);

        if (currentlyActiveItem && currentlyActiveItem.id !== toggledItem.id) {
          // update view locally to reflect the change
          const prevActiveIndex = updatedLocalData.findIndex((item) => item.id === currentlyActiveItem.id);
          if (prevActiveIndex !== -1) {
            updatedLocalData[prevActiveIndex] = { ...updatedLocalData[prevActiveIndex], status: false };
          }
        }
      }

      // adding payload for the toggled item
      payloads.push({
        id: toggledItem.id,
        status: newStatus,
      });
      // update view locally to reflect the change
      updatedLocalData[index] = { ...updatedLocalData[index], status: newStatus };

      const response = await menuServingTimeService.updateList(payloads);

      if (response) {
        setData(updatedLocalData);
        const toastList = [ToastSample.toastSuccess(t('App.info'), t('menuServingTime.modal.update_status'))];
        showGlobalModal(() => (
          <ToastComponent toastList={toastList} position={'bottom-end'} autoDelete={true} autoDeleteTime={2500} />
        ));
      }
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  const isDetailButtonDisabled = selectedItemIds.length !== 1;
  const isDeleteButtonDisabled =
    selectedItemIds.length === 0 ||
    (selectedItemIds.length === 1 && data.filter((item) => !item.delete_flg).length === 1);

  const isDefaultOnItemSelected = data.some((item) => item.status && selectedItemIds.includes(item.id));
  const currentDefaultItem = data.find((item) => item.status);

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <StatusHeader />
      </div>
      <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <div className="page-content store-setting-page d-flex flex-column">
          <div>
            <FeatureTitleBar title={t('menuServingTime.title')} />
          </div>
          <div className="text-end mb-4 mt-4">
            <Button
              ref={btnAddRef}
              className="btn--dark-green ms-3"
              onClick={() => {
                setEditingItemId(null);
                setShowMenuServingTimeDetail(true);
              }}
            >
              {t('menuServingTime.button.add')}
            </Button>
            <Button
              ref={btnDetailRef}
              className="ms-3 "
              onClick={() => {
                if (selectedItemIds.length === 1) {
                  setEditingItemId(selectedItemIds[0]);
                  setShowMenuServingTimeDetail(true);
                }
              }}
              disabled={isDetailButtonDisabled}
            >
              {t('menuServingTime.button.detail')}
            </Button>
            <Button
              ref={btnDeleteRef}
              className="btn--red ms-3 m-5 mt-0 mb-0"
              onClick={() => {
                currentDefaultItem &&
                  showGlobalModal(() => (
                    <ConfirmModal
                      title={t('App.confirm')}
                      message={
                        isDefaultOnItemSelected
                          ? t('menuServingTime.modal.delete_default_on')
                          : t('menuServingTime.modal.delete_default_off')
                      }
                      onConfirm={() => handleConfirmDelete(currentDefaultItem)}
                    />
                  ));
              }}
              disabled={isDeleteButtonDisabled}
            >
              {t('menuServingTime.button.delete')}
            </Button>
          </div>

          <div className="sticky-table flex-grow-1 flex-shrink-1 m-1 mr-1">
            <Table hover>
              <thead>
                <tr>
                  <th rowSpan={2} style={{ width: '5%' }}>
                    {t('menuServingTime.table.choice')}
                  </th>
                  <th rowSpan={2} style={{ width: '25%' }}>
                    {t('menuServingTime.table.name')}
                  </th>
                  <th colSpan={3} style={{ width: '30%' }}>
                    {t('menuServingTime.table.provision_pattern.title')}
                  </th>
                  <th rowSpan={2}>{t('menuServingTime.table.note')}</th>
                  <th rowSpan={2} style={{ width: '5%' }}>
                    {t('menuServingTime.table.status')}
                  </th>
                </tr>
                <tr>
                  <th>{t('menuServingTime.table.provision_pattern.before')}</th>
                  <th>{t('menuServingTime.table.provision_pattern.after')}</th>
                  <th>{t('menuServingTime.table.provision_pattern.together')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => {
                  return renderMenuServingTimeRow(item, index);
                })}
              </tbody>
            </Table>
            {/* Add and Edit modal */}
            {showMenuServingTimeDetail &&
              createPortal(
                <MenuServingTimeDetail
                  menuServingTimeId={editingItemId}
                  onClose={handleCloseMenuServingTimeDetail}
                  onUpdateNewItem={handleReflectUpdateNewItem}
                />,
                document.body
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
export default MenuServingTime;
